import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  Typography,
  message
} from 'antd';
import { useNavigate } from 'react-router-dom';
import type { Route } from '../../../types';
import type { RouteViewModel } from '../../../types/routeViewModel';
import {
  useAddRouteEventMutation,
  useCreateAllocationsMutation,
  useCreateRouteMutation,
  useDeleteRouteMutation,
  useFinalizeOperationalCompletionMutation,
  useGetAvailableCargoQuery,
  useGetExecutionProgressQuery,
  useUpsertRouteStopsMutation
} from '../../../store/api/routesApi';
import { mergeSuccessorContextIntoCreateRouteBody } from '../../../components/routes/RouteCreateForm/successorRouteCreate';
import {
  useGetCarriersQuery,
  useGetDriversQuery,
  useGetVehiclesQuery
} from '../../../store/api/referencesApi';
import { executeRouteCreatePipeline } from '../../routeGroups/routeCreatePipeline';
import type { CorrectionContext } from '../hooks/useCorrectionContext';

const { Text, Paragraph } = Typography;

type Props = {
  route: Route;
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function CarrierReplacementWizard({ route, routeId, view, ctx, open, onClose, onSuccess }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [logistComment, setLogistComment] = useState('');
  const [carrierId, setCarrierId] = useState<number | undefined>();
  const [driverId, setDriverId] = useState<number | undefined>();
  const [vehicleId, setVehicleId] = useState<number | undefined>();
  const [totalAmount, setTotalAmount] = useState<number | undefined>(
    view.carrierFinance?.rateAmount ?? route.totalAmount ?? undefined
  );
  const [prepaymentPercent, setPrepaymentPercent] = useState<number | undefined>(
    view.carrierFinance?.prepaymentPercent ?? route.prepaymentPercent ?? undefined
  );
  const [prepaymentDelayDays, setPrepaymentDelayDays] = useState<number | undefined>(
    view.carrierFinance?.prepaymentDelayBankDays ?? route.prepaymentDelayDays ?? undefined
  );
  const [mainPaymentDelayDays, setMainPaymentDelayDays] = useState<number | undefined>(
    view.carrierFinance?.mainPaymentDelayBankDays ?? route.mainPaymentDelayDays ?? undefined
  );
  const [postCompletionRefreshDone, setPostCompletionRefreshDone] = useState(false);

  const { data: executionProgress, refetch: refetchProgress } = useGetExecutionProgressQuery(routeId, {
    skip: !open
  });

  const executionComplete =
    ctx.correction.executionLock.executionComplete ||
    executionProgress?.progress?.executionComplete ||
    view.executionDashboard?.executionComplete ||
    false;

  const linkedAppId = view.operation.linkedApplicationsSummary[0]?.applicationId;

  const { data: remainderCargo = [], refetch: refetchCargo } = useGetAvailableCargoQuery(
    {
      loadingPointType: 'application',
      loadingPointId: linkedAppId
    },
    { skip: !open || linkedAppId == null || step < 2 }
  );

  const { data: carriers = [] } = useGetCarriersQuery(undefined, { skip: !open });
  const { data: drivers = [] } = useGetDriversQuery(undefined, { skip: !open });
  const { data: vehicles = [] } = useGetVehiclesQuery(undefined, { skip: !open });

  const [successorControlTaskId, setSuccessorControlTaskId] = useState<number | null>(null);
  const [completionLockedWithoutTask, setCompletionLockedWithoutTask] = useState(false);
  const [finalizeCompletion, { isLoading: completing }] = useFinalizeOperationalCompletionMutation();
  const [createRoute] = useCreateRouteMutation();
  const [deleteRoute] = useDeleteRouteMutation();
  const [upsertRouteStopsMutation] = useUpsertRouteStopsMutation();
  const [createAllocations] = useCreateAllocationsMutation();
  const [addEvent] = useAddRouteEventMutation();
  const [creating, setCreating] = useState(false);

  const reset = () => {
    setStep(0);
    setLogistComment('');
    setCarrierId(undefined);
    setDriverId(undefined);
    setVehicleId(undefined);
    setPostCompletionRefreshDone(false);
    setSuccessorControlTaskId(null);
    setCompletionLockedWithoutTask(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    void refetchProgress();
  }, [open, refetchProgress]);

  const requestSuccessorFinalize = async (opts?: { recoverOnly?: boolean }) => {
    const finalized = await finalizeCompletion({
      routeId,
      logistComment: logistComment.trim() || 'Восстановление задачи продолжения маршрута',
      origin: 'ROUTE_CORRECTION',
      correctionCompletionReason: 'CARRIER_REPLACEMENT'
    }).unwrap();
    const taskId = finalized.successorControlTaskId ?? null;
    if (taskId == null || taskId <= 0) {
      setSuccessorControlTaskId(null);
      setCompletionLockedWithoutTask(true);
      throw new Error('SUCCESSOR_TASK_ID_MISSING');
    }
    setSuccessorControlTaskId(taskId);
    setCompletionLockedWithoutTask(false);
    if (!opts?.recoverOnly) {
      message.success('Маршрут завершён');
    } else {
      message.success('Задача продолжения маршрута восстановлена');
    }
    await refetchCargo();
    setPostCompletionRefreshDone(true);
    setStep(2);
    onSuccess();
  };

  const handleFinalize = async () => {
    if (!logistComment.trim()) {
      message.error('Укажите комментарий логиста');
      return;
    }
    try {
      await requestSuccessorFinalize();
    } catch (e: unknown) {
      const err = e as {
        status?: number;
        data?: { error?: string; code?: string };
        message?: string;
      };
      const code = err?.data?.code ?? err?.message;
      if (
        code === 'SUCCESSOR_TASK_ENSURE_FAILED' ||
        code === 'SUCCESSOR_TASK_ID_MISSING' ||
        err?.status === 503
      ) {
        setCompletionLockedWithoutTask(true);
        setSuccessorControlTaskId(null);
        message.error(
          err?.data?.error ??
            'Маршрут завершён, но задача продолжения недоступна. Нажмите «Получить задачу продолжения».'
        );
        setStep(2);
        onSuccess();
        return;
      }
      message.error(err?.data?.error ?? 'Не удалось завершить маршрут');
    }
  };

  const handleRecoverSuccessorTask = async () => {
    try {
      await requestSuccessorFinalize({ recoverOnly: true });
    } catch (e: unknown) {
      const err = e as { data?: { error?: string; code?: string }; message?: string };
      message.error(
        err?.data?.error ??
          'Не удалось получить задачу продолжения. Повторите попытку или обратитесь к администратору.'
      );
      setCompletionLockedWithoutTask(true);
    }
  };

  const handleCreateNewRoute = async () => {
    if (successorControlTaskId == null || successorControlTaskId <= 0) {
      message.error(
        'Нет задачи продолжения маршрута. Сначала нажмите «Получить задачу продолжения».'
      );
      return;
    }
    if (!carrierId || !linkedAppId || remainderCargo.length === 0) {
      message.error('Выберите перевозчика и убедитесь, что есть доступный груз');
      return;
    }
    if (totalAmount == null || totalAmount <= 0) {
      message.error('Укажите ставку нового маршрута');
      return;
    }

    setCreating(true);
    try {
      const firstCargo = remainderCargo[0];
      const app = firstCargo.application;

      const routeRequest: Record<string, unknown> = {
        transportType: route.transportType,
        loadingPointType: 'shipper',
        loadingPointId: linkedAppId,
        unloadingPointType: 'application',
        unloadingPointId: linkedAppId,
        loadingAddress: app?.loadingAddress ?? route.loadingAddress,
        unloadingAddress: app?.unloadingAddress ?? route.unloadingAddress,
        loadingCity: app?.loadingCity ?? route.loadingCity,
        unloadingCity: app?.unloadingCity ?? route.unloadingCity,
        plannedLoadingDate: route.plannedLoadingDate,
        plannedUnloadingDate: route.plannedUnloadingDate,
        clientId: route.clientId,
        carrierId,
        driverId,
        vehicleId,
        totalAmount,
        prepaymentPercent,
        prepaymentDelayDays,
        mainPaymentDelayDays,
        mainPaymentAmount:
          prepaymentPercent != null && totalAmount != null
            ? totalAmount * (1 - prepaymentPercent / 100)
            : undefined,
        cargoIds: remainderCargo.map((c) => c.id),
        responsibleLogistId: route.responsibleLogistId
      };

      const stops = [
        {
          type: 'loading',
          position: 0,
          pointType: 'application',
          pointId: linkedAppId,
          address: routeRequest.loadingAddress,
          city: routeRequest.loadingCity
        },
        {
          type: 'unloading',
          position: 1,
          pointType: 'application',
          pointId: linkedAppId,
          parentLoadingStopId: undefined,
          address: routeRequest.unloadingAddress,
          city: routeRequest.unloadingCity
        }
      ];

      const allocations = remainderCargo.map((c) => ({
        cargoId: c.id,
        quantity: c.availableQuantity ?? c.quantity
      }));

      const result = await executeRouteCreatePipeline({
        routeRequest,
        payloadStops: stops,
        allocations,
        mapAllocationsToStopIds: (items, savedStops) => {
          const loading = savedStops.find((s) => s.position === 0);
          const unloading = savedStops.find((s) => s.position === 1);
          return items.map((a) => ({
            ...a,
            fromStopId: loading?.id,
            toStopId: unloading?.id
          }));
        },
        createRoute: (body) =>
          createRoute(
            mergeSuccessorContextIntoCreateRouteBody(body as object, {
              createContext: 'SUCCESSOR_ROUTE',
              predecessorRouteId: routeId,
              successorControlTaskId
            }) as never
          ),
        upsertRouteStops: (args) => upsertRouteStopsMutation(args),
        createAllocations: (args) => createAllocations(args),
        deleteRoute: (id) => deleteRoute(id)
      });

      await addEvent({
        routeId,
        eventType: 'CARRIER_REPLACEMENT_LINK',
        description: JSON.stringify({
          oldRouteId: routeId,
          newRouteId: result.routeId
        })
      }).unwrap();

      message.success('Новый маршрут создан');
      handleClose();
      navigate(`/routes/${result.routeId}?tab=correction`);
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message ?? 'Не удалось создать новый маршрут');
    } finally {
      setCreating(false);
    }
  };

  if (!executionComplete && open) {
    return (
      <Modal open={open} onCancel={handleClose} footer={<Button onClick={handleClose}>Закрыть</Button>}>
        <Alert
          type="error"
          showIcon
          message="Замена перевозчика недоступна"
          description="Исполнение маршрута не завершено. Дождитесь полного исполнения или завершите оставшиеся операции."
        />
      </Modal>
    );
  }

  return (
    <Modal
      title="Замена перевозчика"
      open={open}
      onCancel={handleClose}
      width={640}
      footer={null}
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Проверка' },
          { title: 'Завершение' },
          { title: 'Новый маршрут' }
        ]}
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Ставка и условия оплаты нового маршрута задаются отдельно и не продолжают договор предыдущего перевозчика."
          />
          <Paragraph>
            Текущий маршрут будет завершён, затем создан новый маршрут с оставшимся грузом и новым перевозчиком.
          </Paragraph>
          <Button type="primary" onClick={() => setStep(1)}>
            Продолжить
          </Button>
        </Space>
      )}

      {step === 1 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Комментарий при завершении текущего маршрута:</Text>
          <textarea
            rows={3}
            style={{ width: '100%' }}
            value={logistComment}
            onChange={(e) => setLogistComment(e.target.value)}
          />
          <Button type="primary" loading={completing} onClick={handleFinalize}>
            Завершить текущий маршрут
          </Button>
        </Space>
      )}

      {step === 2 && (
        <Form layout="vertical">
          {(successorControlTaskId == null || completionLockedWithoutTask) && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Задача продолжения маршрута недоступна"
              description="Создание нового маршрута заблокировано, пока не получена задача CREATE_SUCCESSOR_ROUTE. Повторите получение задачи — повторное завершение маршрута не выполняется."
              action={
                <Button size="small" type="primary" loading={completing} onClick={handleRecoverSuccessorTask}>
                  Получить задачу продолжения
                </Button>
              }
            />
          )}
          {!postCompletionRefreshDone && (
            <Alert type="warning" message="Обновите доступность груза после завершения" />
          )}
          <Form.Item label="Новый перевозчик" required>
            <Select
              showSearch
              optionFilterProp="label"
              value={carrierId}
              onChange={setCarrierId}
              options={carriers.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item label="Водитель">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={driverId}
              onChange={setDriverId}
              options={drivers.map((d) => ({
                value: d.id,
                label: d.fullName || `#${d.id}`
              }))}
            />
          </Form.Item>
          <Form.Item label="ТС">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={vehicleId}
              onChange={setVehicleId}
              options={vehicles.map((v) => ({
                value: v.id,
                label: v.registrationNumber ?? `#${v.id}`
              }))}
            />
          </Form.Item>
          <Form.Item label="Ставка (₽)" required>
            <InputNumber min={0} style={{ width: '100%' }} value={totalAmount} onChange={(v) => setTotalAmount(v ?? undefined)} />
          </Form.Item>
          <Form.Item label="Предоплата, %">
            <InputNumber min={0} max={100} style={{ width: '100%' }} value={prepaymentPercent} onChange={(v) => setPrepaymentPercent(v ?? undefined)} />
          </Form.Item>
          <Form.Item label="Отсрочка предоплаты, дн.">
            <InputNumber min={0} style={{ width: '100%' }} value={prepaymentDelayDays} onChange={(v) => setPrepaymentDelayDays(v ?? undefined)} />
          </Form.Item>
          <Form.Item label="Отсрочка основного платежа, дн.">
            <InputNumber min={0} style={{ width: '100%' }} value={mainPaymentDelayDays} onChange={(v) => setMainPaymentDelayDays(v ?? undefined)} />
          </Form.Item>
          <Text type="secondary">Доступно грузовых позиций: {remainderCargo.length}</Text>
          <Button
            type="primary"
            loading={creating}
            disabled={successorControlTaskId == null || successorControlTaskId <= 0}
            style={{ marginTop: 16 }}
            onClick={handleCreateNewRoute}
          >
            Создать новый маршрут
          </Button>
        </Form>
      )}
    </Modal>
  );
}
